import * as React from "react";
import styled, { css } from "styled-components";
import { useHistory } from "react-router-dom";
import { useMappedState } from "redux-react-hook";
import {
  CellContext,
  ColumnDef
} from "@tanstack/react-table";

import {
  TabbedNavigation,
  TabOptions
} from "src/library/components/TabbedNavigation/TabbedNavigation";
import { Text } from "src/library/components/Text/Text";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { ActionButton } from "src/library/components/Button";
import { IAction } from "src/library/components/Button/ActionButton";
import Table from "src/core/dashboard/components/widgets/table/Table";
import { useWidgetContainerSize } from "src/core/dashboard/hooks";
import { formatPrice } from "src/core/prices/utils";
import {
  Invoice,
  Transaction,
  OutstandingBalanceByDays,
  useAccountingApi
} from "src/core/accounting/api/accounting.api";
import { invoiceTableColumns } from "src/core/shared/components/tables/invoiceTableColumns";
import { transactionTableColumns } from "src/core/shared/components/tables/transactionTableColumns";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import type { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";

const WIDGET_ROW_LIMIT = 4;

type BalanceDaysOption = "total" | "days7" | "days14" | "days30" | "days60" | "days90";

const BALANCE_OPTIONS: Array<{ value: BalanceDaysOption; label: string }> = [
  {
    value: "total",
    label: "All outstanding"
  },
  {
    value: "days7",
    label: "Due in 7 days"
  },
  {
    value: "days14",
    label: "Due in 14 days"
  },
  {
    value: "days30",
    label: "Due in 30 days"
  },
  {
    value: "days60",
    label: "Due in 60 days"
  },
  {
    value: "days90",
    label: "Due in 90 days"
  }
];

/**
 * customer facing accounting widget
 * @returns
 */
const AccountingDashboardWidget: React.ReactFCWithChildren = () => {
  const history = useHistory();
  const wrapperRef = React.createRef<HTMLDivElement>();
  const { size } = useWidgetContainerSize(wrapperRef);

  const { currentRole, masquerade } = useMappedState(({ global }: IStore) => ({
    currentRole: global.role,
    masquerade: global.masqueradeMode
  }));

  const [ { drop } ] = useDrop();
  const isAdmin = currentRole?.type === "admin"; //&& !masquerade?.active;
  const [ invoicesResponse, getInvoicesApi ] = useAccountingApi("LIST_CUSTOMER_INVOICES");
  const [ transactionsResponse, getTransactionsApi ] = useAccountingApi("LIST_CUSTOMER_TRANSACTIONS");
  const [ activeTab, setActiveTab ] = React.useState<string>("invoices");
  const [ selectedBalanceDays, setSelectedBalanceDays ] = React.useState<BalanceDaysOption>("total");

  // Invoice action button
  const renderInvoiceActionButton = React.useCallback((info: CellContext<Invoice, unknown>) => {
    const invoice = info.row.original;
    const invoiceUrl = isAdmin ? `/admin/accounting/invoices/${invoice.id}` : `/accounting/invoices/${invoice.id}`;
    //const checkoutUrl = isAdmin ? `/admin/accounting/invoices/${invoice.id}/checkout` : `/accounting/invoices/${invoice.id}/checkout`;

    const actions: IAction[] = [
      {
        title: "View",
        action: () => {
          history.push(invoiceUrl);
        },
        newTabPath: invoiceUrl
      }
      // {
      //   title: "Pay Now",
      //   icon: Icons.CREDIT,
      //   action: () => {
      //     history.push(checkoutUrl);
      //   },
      //   newTabPath: checkoutUrl
      // }
    ];

    return (
      <ActionButton
        title="Actions"
        items={actions}
      />
    );
  }, [ history, isAdmin ]);

  // Transaction action button - simple button matching Accounting page pattern
  const renderTransactionActionButton = React.useCallback((info: CellContext<Transaction, unknown>) => {
    const transaction = info.row.original;
    // Use role-based URL: admin uses /admin/accounting/transactions, customer uses /accounting/transactions
    const transactionUrl = isAdmin ? `/admin/accounting/transactions/${transaction.id}` : `/accounting/transactions/${transaction.id}`;

    const actions: IAction[] = [
      {
        title: "View",
        action: () => {
          history.push(transactionUrl);
        },
        newTabPath: transactionUrl
      }

    ];

    return (
      <ActionButton
        title="Actions"
        items={actions}
      />
    );
  }, [ history, isAdmin ]);

  const invoiceColumns: Array<ColumnDef<Invoice, keyof Invoice>> = React.useMemo(() => {
    return invoiceTableColumns({
      showActions: true,
      actionButton: renderInvoiceActionButton
    }) as Array<ColumnDef<Invoice, keyof Invoice>>;
  }, [ renderInvoiceActionButton ]);

  const transactionColumns: Array<ColumnDef<Transaction, keyof Transaction>> = React.useMemo(() => {
    return transactionTableColumns({
      showActions: true,
      actionButton: renderTransactionActionButton
    }) as Array<ColumnDef<Transaction, keyof Transaction>>;
  }, [ renderTransactionActionButton ]);

  const onFetchInvoices = React.useCallback((params: {
    filters: FilterParams<Invoice>,
    pagination: PaginatedParams<Invoice>
  }) => {
    if (!drop?.customerId) {
      return;
    }

    getInvoicesApi({
      customerId: drop.customerId,
      filters: params.filters,
      pagination: params.pagination
    });
  }
  , [ drop?.customerId, getInvoicesApi ]);

  const onFetchTransactions = React.useCallback((params: {
    filters: FilterParams<Transaction>,
    pagination: PaginatedParams<Transaction>
  }) => {
    if (!drop?.customerId) {
      return;
    }

    getTransactionsApi({
      customerId: drop.customerId,
      filters: params.filters,
      pagination: params.pagination
    });
  }, [ drop?.customerId, getTransactionsApi ]);

  const invoiceData = React.useMemo(() => {
    return invoicesResponse.data?.invoices?.result || [];
  }, [ invoicesResponse.data?.invoices?.result ]);

  const transactionData = React.useMemo(() => {
    return transactionsResponse.data?.transactions?.result || [];
  }, [ transactionsResponse.data?.transactions?.result ]);

  // Get the appropriate balance based on selected days option
  const getSelectedBalance = React.useCallback(
    (balanceByDays: OutstandingBalanceByDays | undefined, fallback: number): number => {
      if (!balanceByDays) {
        return fallback;
      }

      return balanceByDays[ selectedBalanceDays ] ?? fallback;
    },
    [ selectedBalanceDays ],
  );

  const outstandingBalance = getSelectedBalance(invoicesResponse.data?.outstandingBalanceByDays, invoicesResponse.data?.outstandingBalance || 0);

  const InvoicesTabContent = () => (
    <TabContentWrapper>
      <TabHeader>
        <HeaderLeft>
          <Icon
            icon={Icons.NOTES}
            size={20}
            $fillColor="primaryLight"
          />

          <Text
            fontSize="h4"
            weight="bold"
          >
            Outstanding Invoices
          </Text>
        </HeaderLeft>

        <OutstandingBalanceWrapper>
          <Text
            fontSize="p"
            weight="bold"
          >
            Balance due:
          </Text>

          <BalanceSelect
            value={selectedBalanceDays}
            onChange={e => setSelectedBalanceDays(e.target.value as BalanceDaysOption)}
          >
            {BALANCE_OPTIONS.map(option => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </BalanceSelect>

          <OutstandingBalance
            fontSize="p"
            weight="bold"
          >
            {formatPrice(outstandingBalance)}
          </OutstandingBalance>
        </OutstandingBalanceWrapper>
      </TabHeader>

      <TableWrapper>
        <Table
          tableOf="Invoices"
          hideHeader
          className={size}
          data={invoiceData}
          columns={invoiceColumns}
          onFetchData={onFetchInvoices}
          defaultPagination={{
            offset: 0,
            limit: WIDGET_ROW_LIMIT,
            page: 0,
            sort: [ [ "outstandingAmount", "DESC" ] ] as Array<[string, "DESC" | "ASC"]>
          }}
          loading={invoicesResponse.pending}
          pageCount={0}
          rowCount={0}
          pageSizeOptions={[]}
          error={invoicesResponse.error}
        />
      </TableWrapper>

      <ViewAllFooter>
        <ViewAllLink onClick={() => history.push(isAdmin ? "/admin/accounting" : "/accounting")}>
          View All
        </ViewAllLink>
      </ViewAllFooter>
    </TabContentWrapper>
  );

  const TransactionsTabContent = () => (
    <TabContentWrapper>
      <TabHeader>
        <HeaderLeft>
          <Icon
            icon={Icons.CREDIT}
            size={20}
            $fillColor="primaryLight"
          />

          <Text
            fontSize="h4"
            weight="bold"
          >
            Transactions
          </Text>
        </HeaderLeft>
      </TabHeader>

      <TableWrapper>
        <Table
          tableOf="Transactions"
          hideHeader
          className={size}
          data={transactionData}
          columns={transactionColumns}
          onFetchData={onFetchTransactions}
          defaultPagination={{
            offset: 0,
            limit: WIDGET_ROW_LIMIT,
            page: 0,
            sort: [ [ "transactionDate", "DESC" ] ] as Array<[string, "DESC" | "ASC"]>
          }}
          loading={transactionsResponse.pending}
          pageCount={0}
          rowCount={0}
          pageSizeOptions={[]}
          error={transactionsResponse.error}
        />
      </TableWrapper>

      <ViewAllFooter>
        <ViewAllLink onClick={() => history.push(isAdmin ? "/admin/accounting" : "/accounting")}>
          View All
        </ViewAllLink>
      </ViewAllFooter>
    </TabContentWrapper>
  );

  const tabs: TabOptions[] = [
    {
      title: "Outstanding Invoices",
      name: "invoices",
      content: InvoicesTabContent,
      pad: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
      }
    },
    {
      title: "Transactions",
      name: "transactions",
      content: TransactionsTabContent,
      pad: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
      }
    }
  ];

  return (
    <WidgetWrapper ref={wrapperRef}>
      <WidgetTabbedNavigation
        tabs={tabs}
        defaultTab={activeTab}
        amendDefaultTab={setActiveTab}
      />
    </WidgetWrapper>
  );
};

export default AccountingDashboardWidget;

const WidgetWrapper = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const WidgetTabbedNavigation = styled(TabbedNavigation)`
  ${({ theme }) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .tab-options {
      flex-shrink: 0;
      
      .tab-option {
        /* Use border-top for the highlight instead of ::before pseudo-element */
        /* This avoids the overflow: hidden clipping issue in the dashboard container */
        border-top: 0.4rem solid transparent;
        transition: border-top-color ease-in-out 0.4s;

        /* Hide the original ::before pseudo-element */
        &::before {
          display: none;
        }

        &.active {
          border-top-color: ${theme.colors.primaryDark};
        }

        &:not(.active):hover {
          border-top-color: ${theme.colors.primaryLight};
        }
      }
    }

    .tab-content {
      padding: 0;
      margin-bottom: 0;
      border: none;
      flex: 1;
      overflow: hidden;
    }
  `}
`;

const TabContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.padding.md};
  background: ${({ theme }) => theme.colors.lightestGrey};
  border-right:  0.1rem solid #D9DCE0;
  border-left:   0.1rem solid #D9DCE0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.margins.sm};
`;

const OutstandingBalanceWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.margins.sm};
`;

const OutstandingBalance = styled(Text)`
  color: ${({ theme }) => theme.colors.error};
`;

const BalanceSelect = styled.select`
  appearance: none;
  padding: ${({ theme }) => theme.spacing.padding.xs} ${({ theme }) => theme.spacing.padding.md};
  padding-right: ${({ theme }) => theme.spacing.padding.lg};
  font-size: ${({ theme }) => theme.fonts.sizes.p};
  font-weight: ${({ theme }) => theme.fonts.weights.bold};
  color: ${({ theme }) => theme.colors.error};
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: ${({ theme }) => theme.borderRadius};
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primaryLight};
  }
`;

const TableWrapper = styled.div`
  overflow-y: scroll;
  
  /* Ensure table content doesn't overflow the widget */
  table {
    table-layout: fixed;
  }
  
  /* Constrain action buttons within their cells */
  td:last-child {
    overflow: hidden;
  }
`;

const ViewAllFooter = styled.div`
  display: none;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.padding.md};
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
`;

const ViewAllLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primaryLight};
  font-size: ${({ theme }) => theme.fonts.sizes.p};
  font-weight: ${({ theme }) => theme.fonts.weights.bold};
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;
